import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

/** One municipality of fleet-wide work focus (summed member top areas). */
export type PortfolioGeoArea = {
  code: string
  name: string
  /** Located tracked events attributed to the municipality, last 28 days. */
  count: number
}

/**
 * Sums every member's top work municipalities across the whole portfolio,
 * busiest first. Each profile carries only its own top municipalities (the
 * sync caps them per user), so this is the fleet's aggregate FOCUS — where
 * the tracked work concentrates — not an exact total per municipality.
 */
export function deriveFleetGeoAreas(
  customers: PortfolioCustomerSummary[],
): PortfolioGeoArea[] {
  const byCode = new Map<string, { name: string; count: number }>()
  for (const customer of customers) {
    for (const member of customer.members) {
      for (const { Code, Name, Count } of member.TopGeoAreas28d) {
        const entry = byCode.get(Code)
        if (entry) entry.count += Count
        else byCode.set(Code, { name: Name, count: Count })
      }
    }
  }
  return [...byCode.entries()]
    .map(([code, { name, count }]) => ({ code, name, count }))
    .sort((a, b) => b.count - a.count)
}

/** One sign-in place resolved to a Norwegian municipality centroid. */
export type PortfolioSignInPlace = {
  code: string
  name: string
  signIns: number
  users: number
  lat: number
  long: number
}

export type PortfolioSignInGeo = {
  matched: PortfolioSignInPlace[]
  /** Labels that resolved to no municipality (foreign cities, sub-places). */
  unmatched: { location: string; signIns: number }[]
}

type SignInLocationRow = {
  Location: string
  Country?: string | null
  SignInCount: number
  UserCount: number
}

type MunicipalityRow = {
  Code: string
  Name: string
  Lat: number
  Long: number
}

/**
 * Places sign-in location labels on the map by matching the city part of
 * the Norwegian labels ("Oslo, Norway" → "Oslo") against municipality names.
 * GeoIP cities usually ARE municipality names in Norway; sub-places and
 * foreign cities fall out into `unmatched` and are listed as text instead
 * of silently dropped.
 */
export function matchSignInLocations(
  counts: SignInLocationRow[],
  municipalities: MunicipalityRow[],
): PortfolioSignInGeo {
  const byName = new Map(
    municipalities.map((municipality) => [
      municipality.Name.toLowerCase(),
      municipality,
    ]),
  )

  const matchedByCode = new Map<string, PortfolioSignInPlace>()
  const unmatched: { location: string; signIns: number }[] = []
  for (const row of counts) {
    const city = row.Location.split(",")[0]?.trim().toLowerCase() ?? ""
    const municipality =
      row.Country === "NO" && city !== "" ? byName.get(city) : undefined
    if (!municipality) {
      unmatched.push({ location: row.Location, signIns: row.SignInCount })
      continue
    }
    const entry = matchedByCode.get(municipality.Code)
    if (entry) {
      entry.signIns += row.SignInCount
      entry.users += row.UserCount
    } else {
      matchedByCode.set(municipality.Code, {
        code: municipality.Code,
        name: municipality.Name,
        signIns: row.SignInCount,
        users: row.UserCount,
        lat: municipality.Lat,
        long: municipality.Long,
      })
    }
  }

  return {
    matched: [...matchedByCode.values()].sort((a, b) => b.signIns - a.signIns),
    unmatched: unmatched.sort((a, b) => b.signIns - a.signIns),
  }
}
